import api from './api';
import type { PspLuCodeCheck, PspInactiveOutcome, PspOutcome, PspPhase, PspRoute, PspStatusFilter } from '../constants/psp';

/**
 * PRX-129 — the PSP queue and prior-authorization workflow (ADR-117). Every
 * route carries the pharmacy in the path and is authorised against it by the
 * backend's strict guard. A PSP file IS a specialty-drug row: the queue and the
 * patient's Insurance & PSP card read the same `PspFile`.
 */
export interface PspNextAction {
  /** `<transition>` or `<transition>.<variant>`. */
  id: string;
  transition: string;
  /** The variant's fixed payload, sent as is. */
  preset: Record<string, string> | null;
  label: string;
  /** True when the step needs input (or a confirmation) before it is sent. */
  dialog: boolean;
  /** WP-3: why the server will refuse the step today (expired or undated blood work), or null. */
  blockedReason?: string | null;
}

/** WP-3 (Q19). `on` and `validUntil` are YYYY-MM-DD; both null unless dated blood work is done. */
export interface PspBloodwork {
  status: 'requested' | 'done' | 'not_needed';
  on: string | null;
  validUntil: string | null;
  /** True from the day after `validUntil`. */
  expired: boolean;
}

export interface PspPlanRef {
  bin: string;
  carrierId: string;
  planName: string;
}

export interface PspFile {
  id: string;
  /** Send back as `expectedVersion`. */
  version: number;
  /** `readOnly`: an inactivated file; only the closing outcomes, cancel, prescriber informed and notes go through. */
  patient: { compositeId: string | null; name: string | null; readOnly: boolean };
  drugName: string;
  rxNumber: string | null;
  prescriberName: string | null;
  route: PspRoute | null;
  plan: PspPlanRef | null;
  /** PRX-147: the server's check of the latest T10 LU code; `not_checked` / `not_on_file` are flagged. */
  luCodeCheck: PspLuCodeCheck | null;
  /** PRX-147 postponed: the server runs the T10 LU quiz (`PSP_LU_QUIZ_ENABLED`). Off, T10 takes the plain LU code. */
  luQuizEnabled: boolean;
  state: string | null;
  stateLabel: string | null;
  phase: PspPhase | null;
  attempts: number;
  /** YYYY-MM-DD */
  receivedDate: string | null;
  lastUpdateAt: string | null;
  /** YYYY-MM-DD (pharmacy day), or null: "No SLA set", closed, `slaNeedsReceivedDate` or `slaDateInvalid`. */
  dueDate: string | null;
  /** Business days. */
  slaDays: number | null;
  /** Business days past the due date. */
  daysLate: number | null;
  /** The SLA counts from the received date and the file has none: "No received date". */
  slaNeedsReceivedDate: boolean;
  /** The stored received date is not a real date: "Received date could not be read". */
  slaDateInvalid: boolean;
  legacyStage: string | null;
  inWorkflow: boolean;
  closed: boolean;
  /** Its history could not be read: no step is offered until it is fixed. */
  needsReview: boolean;
  /** The PSP workflow runs at this pharmacy (the server's rule); off it, the steps are hidden. */
  workflowEnabled: boolean;
  pendingOutcome: PspOutcome | null;
  /** WP-3: the file was ended as an inactive patient's, by this outcome. Kept once closed. */
  inactiveOutcome: PspInactiveOutcome | null;
  /** WP-3: at "Rejection letter needed", the secondary plan the letter is needed for. */
  rejectionLetterFor: PspRoute | null;
  /** WP-3: the blood work as last recorded; null before any, and on a closed file. */
  bloodwork: PspBloodwork | null;
  renewalDate: string | null;
  requiresRenewal: boolean;
  nextActions: { primary: PspNextAction | null; more: PspNextAction[] };
  legacyAdoption: {
    suggested: string | null;
    suggestedOutcome: PspOutcome | null;
    allowed: Array<{ state: string; label: string }>;
  } | null;
}

export interface PspTimelineEntry {
  id: string;
  sequence: number;
  eventType: string;
  /** The step's button label, e.g. "No answer". */
  stepLabel: string;
  fromState: string | null;
  toState: string;
  toStateLabel: string;
  payload: Record<string, unknown>;
  note: string | null;
  occurredAt: string;
  actorUserName: string | null;
}

export interface PspFileDetail {
  file: PspFile;
  timeline: PspTimelineEntry[];
}

export interface PspQueue {
  items: PspFile[];
  total: number;
  page: number;
  pageSize: number;
  counts: { ongoing: number; overdue: number; noReceivedDate: number; notInWorkflow: number };
}

export interface PspQueueFilters {
  status: PspStatusFilter;
  phase?: PspPhase | null;
  overdue?: boolean;
  /** Only the files whose SLA is waiting on a received date. */
  noReceivedDate?: boolean;
  page: number;
  pageSize: number;
}

export interface OpenPspFileBody {
  rxNumber: string;
  drugName: string;
  /** YYYY-MM-DD */
  receivedDate: string;
  note?: string;
}

export interface PspTransitionBody {
  transition: string;
  expectedVersion: number;
  payload?: Record<string, unknown>;
  note?: string;
}

/** PRX-147: one ODB limited-use criterion; the text is decoded and may hold line breaks. */
export interface OdbLuCriterion {
  code: string;
  criteria: string;
}

/**
 * PRX-147: a DIN's ODB limited-use criteria, read live from pgdata. `none` is
 * "No LU criteria on file for this DIN"; `not_listed` is a DIN pgdata does not
 * list on the ODB formulary. pgdata unreadable is a 503, never one of these.
 */
export type OdbLuReference =
  | { status: 'found'; din: string; drugName: string | null; builtAt: string | null; criteria: OdbLuCriterion[] }
  | { status: 'none' | 'not_listed'; din: string; drugName: string | null; builtAt: string | null };

// Every id is one path segment (encoded), so no id can reach another route.
const seg = encodeURIComponent;
const base = (pharmacyId: string) => `/api/psp/pharmacies/${seg(pharmacyId)}`;

export const pspService = {
  async list(pharmacyId: string, filters: PspQueueFilters): Promise<PspQueue> {
    const params: Record<string, string | number> = { status: filters.status };
    if (filters.phase) params.phase = filters.phase;
    if (filters.overdue) params.overdue = 'true';
    if (filters.noReceivedDate) params.noReceivedDate = 'true';
    params.page = filters.page;
    params.pageSize = filters.pageSize;
    const response = await api.get<PspQueue>(`${base(pharmacyId)}/files`, { params });
    return response.data;
  },

  /** Every specialty row of the patient, rows not in the workflow included. */
  async listForPatient(pharmacyId: string, patientId: string): Promise<{ items: PspFile[] }> {
    const response = await api.get<{ items: PspFile[] }>(`${base(pharmacyId)}/patients/${seg(patientId)}/files`);
    return response.data;
  },

  async get(pharmacyId: string, fileId: string): Promise<PspFileDetail> {
    const response = await api.get<PspFileDetail>(`${base(pharmacyId)}/files/${seg(fileId)}`);
    return response.data;
  },

  /** Mark as specialty: one PSP file per Rx (a second open file for the Rx is a 409). */
  async openFile(pharmacyId: string, patientId: string, body: OpenPspFileBody): Promise<PspFile> {
    const response = await api.post<PspFile>(`${base(pharmacyId)}/patients/${seg(patientId)}/files`, body);
    return response.data;
  },

  /** PRX-147: the LU criteria the T10 quiz shows. 503 = "LU criteria could not be read". */
  async luCriteria(pharmacyId: string, din: string): Promise<OdbLuReference> {
    const response = await api.get<OdbLuReference>(`${base(pharmacyId)}/odb-lu-criteria/${seg(din)}`);
    return response.data;
  },

  /** One step. A stale `expectedVersion` or a step the file does not allow is a 409 with a fixed sentence. */
  async transition(pharmacyId: string, fileId: string, body: PspTransitionBody): Promise<PspFile> {
    const response = await api.post<PspFile>(`${base(pharmacyId)}/files/${seg(fileId)}/transitions`, body);
    return response.data;
  },
};

/** The query keys, so the queue and the patient card refresh each other. */
export const pspKeys = {
  all: ['psp'] as const,
  queue: (pharmacyId: string | undefined, filters: PspQueueFilters) => ['psp', pharmacyId, 'queue', filters] as const,
  patient: (pharmacyId: string | undefined, patientId: string | undefined) => ['psp', pharmacyId, 'patient', patientId] as const,
  file: (pharmacyId: string | undefined, fileId: string | undefined) => ['psp', pharmacyId, 'file', fileId] as const,
  luCriteria: (pharmacyId: string | undefined, din: string) => ['psp', pharmacyId, 'lu-criteria', din] as const,
};

export default pspService;
